import React, {useCallback, useEffect, useState} from "react";
import {DatePicker, Select, Tag} from "antd";
import {ShieldAlert} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {AppDispatch, RootState} from "../../redux/store";
import {getAuditTrails} from "../../redux/slice/auditTrailSlice";
import {HeroHeader, Card, SearchField, COLORS} from "../../designSystem";

const {RangePicker} = DatePicker;

const methodOptions = [
  {label: "All methods", value: ""},
  {label: "GET", value: "GET"},
  {label: "POST", value: "POST"},
  {label: "PUT", value: "PUT"},
  {label: "PATCH", value: "PATCH"},
  {label: "DELETE", value: "DELETE"},
];

const riskOptions = [
  {label: "All risks", value: ""},
  {label: "Low", value: "Low"},
  {label: "High", value: "High"},
  {label: "Critical", value: "Critical"},
];

const stateOptions = [
  {label: "All activity", value: ""},
  {label: "Errors only", value: "error"},
  {label: "Blocked only", value: "blocked"},
];

const riskColor = (value: string) => {
  if (value === "Critical") return "red";
  if (value === "High") return "volcano";
  return "green";
};

const statusColor = (status: number) => {
  if (status >= 500) return "red";
  if (status >= 400) return "orange";
  if (status >= 300) return "blue";
  return "green";
};

const dateCell = (value: string) => value ? new Date(value).toLocaleString() : "-";

function AuditTrails() {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const {token} = useSelector((state: RootState) => state.account);
  const {records, summary, pageInfo, loading} = useSelector((state: RootState) => state.auditTrail);
  const [searchParam, setSearchParam] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [method, setMethod] = useState("");
  const [riskLevel, setRiskLevel] = useState("");
  const [activityState, setActivityState] = useState("");
  const [statusCode, setStatusCode] = useState("");
  const [dateRange, setDateRange] = useState<any>(null);

  const load = useCallback((pageNumber = 1) => {
    if (!token) return;

    dispatch(getAuditTrails({
      token,
      searchParam,
      userEmail,
      method,
      riskLevel,
      statusCode,
      isError: activityState === "error" ? true : "",
      isBlocked: activityState === "blocked" ? true : "",
      startDate: dateRange?.[0]?.startOf("day")?.toISOString(),
      endDate: dateRange?.[1]?.endOf("day")?.toISOString(),
      pageNumber,
    }));
  }, [token, dispatch, searchParam, userEmail, method, riskLevel, statusCode, activityState, dateRange]);

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={ShieldAlert}
          title="Audit Trail"
          subtitle={`${summary?.totalRecords || 0} recorded journeys · ${summary?.blockedCount || 0} blocked`}
        />

        <div className="grid gap-4 md:grid-cols-4 mt-8">
          <Card className="p-5">
            <div className="text-sm text-slate-500">Total Requests</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.brand}}>{summary?.totalRequests || 0}</div>
            <div className="text-sm text-slate-500">In selected range</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Errors</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.danger}}>{summary?.errorCount || 0}</div>
            <div className="text-sm text-slate-500">HTTP 4xx/5xx</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Blocked</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.warning}}>{summary?.blockedCount || 0}</div>
            <div className="text-sm text-slate-500">Security policy</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Avg Duration</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.text}}>{summary?.averageDurationMs || 0}ms</div>
            <div className="text-sm text-slate-500">{summary?.highRiskCount || 0} high risk</div>
          </Card>
        </div>

        <Card className="mt-8 p-5">
          <div className="grid gap-3 md:grid-cols-6">
            <SearchField
              className="md:col-span-2"
              placeholder="Search path, email, trace, reason"
              value={searchParam}
              onChange={(event) => setSearchParam(event.target.value)}
            />
            <SearchField
              placeholder="User email"
              value={userEmail}
              onChange={(event) => setUserEmail(event.target.value)}
            />
            <Select options={methodOptions} value={method} onChange={setMethod}/>
            <Select options={riskOptions} value={riskLevel} onChange={setRiskLevel}/>
            <Select options={stateOptions} value={activityState} onChange={setActivityState}/>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-4">
            <SearchField
              placeholder="Status code"
              value={statusCode}
              onChange={(event) => setStatusCode(event.target.value)}
            />
            <RangePicker onChange={setDateRange} className="md:col-span-2"/>
          </div>

          <GenericTable
            loading={loading}
            pageInfo={pageInfo}
            data={records}
            handlePageChange={load}
            columns={[
              {title: "User", key: "user", render: (_: any, row: any) => row.userEmail || row.userName || "Anonymous"},
              {title: "Method", dataIndex: "httpMethod", key: "httpMethod", render: (value: string) => <Tag>{value}</Tag>},
              {title: "Path", dataIndex: "path", key: "path"},
              {title: "Status", dataIndex: "statusCode", key: "statusCode", render: (value: number) => <Tag color={statusColor(value)}>{value}</Tag>},
              {title: "Risk", dataIndex: "riskLevel", key: "riskLevel", render: (value: string) => <Tag color={riskColor(value)}>{value || "Low"}</Tag>},
              {title: "Blocked", dataIndex: "isBlocked", key: "isBlocked", render: (value: boolean) => value ? <Tag color="red">Yes</Tag> : <Tag>No</Tag>},
              {title: "Duration", dataIndex: "durationMs", key: "durationMs", render: (value: number) => `${value || 0}ms`},
              {title: "Created", dataIndex: "createdAt", key: "createdAt", render: dateCell},
              {title: "Action", key: "action", render: (_: any, row: any) => (
                <button
                  onClick={() => navigate(`/audit-trails/${row.id}`)}
                  className="font-semibold"
                  style={{color: COLORS.brand}}
                >
                  View
                </button>
              )},
            ]}
          />
        </Card>
      </main>
    </AdminLayout>
  );
}

export default AuditTrails;
